/**
 * Canonical Component_Catalog (Component Access Control, Decision 6, Req 1).
 *
 * The frontend owns the richest view of each Component (its Dashboard_Card,
 * canonical entry route, nav entry, Config_Sub_Routes, and legacy redirects),
 * so the canonical catalog lives here and is vendored to the backends
 * (`componentCatalog.js`) and kept in sync by a golden contract test that pins
 * {@link CATALOG_CONTENT_HASH}.
 *
 * The catalog is the single source of truth for:
 *   - the Access_Matrix / User_Group_Access_Section rows (`inMatrix: true`),
 *   - Dashboard_Card gating (`dashboardCardKey`),
 *   - route + nav gating (`entryRoute`, `configSubRoutes`, `navPath`,
 *     `legacyRedirects`),
 *   - the backend Entry_Gate (`componentId`, `guards`).
 *
 * It enumerates exactly twelve Components: nine Feature_Card_Components
 * (`inMatrix: true`), one owner-only Feature (`inMatrix: false`, HR), and two
 * Landing_Surfaces (`inMatrix: false`).
 */

/**
 * A single gateable Component in the canonical catalog.
 *
 * See Decision 6 for field semantics.
 */
export interface ComponentCatalogEntry {
  /** Stable id, e.g. `'worklists'`, `'reminders'`, `'metrics'`. */
  componentId: string;
  /** Matrix row + card label. */
  title: string;
  /** Feature card vs. landing surface (Req 22). */
  kind: 'feature' | 'landing';
  /**
   * `true` => editable row in the Access_Matrix; `false` => fixed
   * role-defaulted (Landing_Surfaces, Req 22.4).
   */
  inMatrix: boolean;
  /**
   * The Component's card-family label, or `null` for non-card Components.
   *
   * For nine Components it equals the key of their one `DOMAIN_CARDS` entry.
   * For `billing` it names a family and is no `DOMAIN_CARDS` key: the family is
   * `billing-charges` (and `billing-collections` once billing-dashboard-cards
   * task 12.1 lands). Card-family membership is matched by `componentId`, not
   * by key equality, and no runtime code reads this field: gating uses each
   * entry's `componentId`. It is catalog content, so the content hash and the
   * vendored copies carry it, and tests pin its per-Component values.
   *
   * The price of billing-dashboard-cards Req 9.2: this field names a card for
   * nine Components and a family for one. The alternative, an array of card
   * keys here, is a value change, so it would repin the content hash across
   * two backends and two golden fixtures.
   */
  dashboardCardKey: string | null;
  /** Canonical top-level Component_Entry_Route (Req 19.1). */
  entryRoute: string;
  /** Owner-only Config_Sub_Routes (Req 1.3, 15.2). */
  configSubRoutes: string[];
  /** Legacy paths that redirect to {@link entryRoute} (Req 19.2). */
  legacyRedirects: string[];
  /** Nav path, or `null` when the Component has no nav entry. */
  navPath: string | null;
  /** Which service/endpoint(s) the Entry_Gate wraps for this Component. */
  guards: { service: string; endpoints: string[] }[];
}

/**
 * The canonical catalog (Decision 6 table). Order is the rendered order.
 *
 * Nine Feature_Card_Components (`inMatrix: true`), one owner-only Feature
 * (`inMatrix: false`, HR), followed by two Landing_Surfaces (`inMatrix: false`).
 */
export const COMPONENT_CATALOG: readonly ComponentCatalogEntry[] = [
  {
    componentId: 'surveys',
    title: 'Surveys',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'surveys',
    entryRoute: '/surveys',
    configSubRoutes: ['/configure/surveys/**'],
    legacyRedirects: ['/analytics/surveys'],
    navPath: '/surveys',
    guards: [],
  },
  {
    componentId: 'visits',
    title: 'Visits',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'visits',
    entryRoute: '/visits',
    configSubRoutes: ['/configure/visits'],
    legacyRedirects: ['/analytics/visits'],
    navPath: '/visits',
    guards: [],
  },
  {
    componentId: 'provider-scorecards',
    title: 'Provider Scorecards',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'provider-scorecards',
    entryRoute: '/provider-scorecard',
    configSubRoutes: [],
    legacyRedirects: ['/analytics/provider-scorecard'],
    navPath: '/provider-scorecard',
    guards: [],
  },
  {
    componentId: 'metrics',
    title: 'Metrics',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'metrics',
    entryRoute: '/metrics',
    configSubRoutes: ['/metrics/computed-metrics', '/admin/metrics'],
    legacyRedirects: [
      '/analytics/metrics',
      '/analytics/metrics/:metricId',
      '/analytics/computed-metrics/:id',
      '/computed-metrics',
      '/computed-metrics/:id',
      '/configure/metrics',
      '/configure/computed-metrics',
    ],
    navPath: '/metrics',
    guards: [],
  },
  {
    componentId: 'alarms',
    title: 'Alarms & Findings',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'alarms',
    entryRoute: '/alarms',
    configSubRoutes: ['/admin/alarm-daily-report-scheduler'],
    legacyRedirects: ['/analytics/heuristics'],
    navPath: '/alarms',
    guards: [],
  },
  {
    componentId: 'worklists',
    title: 'Worklists',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'worklists',
    entryRoute: '/worklists',
    configSubRoutes: [],
    legacyRedirects: [],
    navPath: '/worklists',
    guards: [],
  },
  {
    componentId: 'goals',
    title: 'Goals & Tasks',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'goalSets',
    entryRoute: '/goal-sets',
    configSubRoutes: [],
    legacyRedirects: [],
    navPath: '/goal-sets',
    guards: [],
  },
  {
    componentId: 'reminders',
    title: 'Reminders',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'reminders',
    entryRoute: '/reminders',
    configSubRoutes: ['/configure/email-reports'],
    legacyRedirects: [],
    navPath: '/reminders',
    guards: [],
  },
  {
    componentId: 'billing',
    title: 'Billing & Collections',
    kind: 'feature',
    inMatrix: true,
    dashboardCardKey: 'billing',
    entryRoute: '/billing',
    configSubRoutes: ['/configure/billing'],
    legacyRedirects: [],
    navPath: '/billing',
    guards: [],
  },
  {
    componentId: 'hr',
    title: 'HR',
    kind: 'feature',
    inMatrix: false,
    dashboardCardKey: 'hr',
    entryRoute: '/hr',
    configSubRoutes: ['/configure/hr'],
    legacyRedirects: [],
    navPath: '/hr',
    guards: [],
  },
  {
    componentId: 'dashboard',
    title: 'Dashboard',
    kind: 'landing',
    inMatrix: false,
    dashboardCardKey: null,
    entryRoute: '/home',
    configSubRoutes: [],
    legacyRedirects: ['/analytics'],
    navPath: '/home',
    guards: [],
  },
  {
    componentId: 'data',
    title: 'Data Management',
    kind: 'landing',
    inMatrix: false,
    dashboardCardKey: null,
    entryRoute: '/data',
    configSubRoutes: ['/configure/data/**'],
    legacyRedirects: [],
    navPath: '/data',
    guards: [],
  },
] as const;

/**
 * The set of all `componentId`s known to the catalog.
 *
 * Used by the Entry_Gate to ignore stale stored policy entries whose
 * `componentId` is no longer in the catalog (Req 1.5).
 */
export const knownComponentIds: ReadonlySet<string> = new Set(
  COMPONENT_CATALOG.map((entry) => entry.componentId),
);

/**
 * Returns the catalog entries that are editable rows in the Access_Matrix
 * (the nine Feature_Card_Components). Landing_Surfaces (`inMatrix: false`)
 * are excluded (Req 22.4, Req 11.1).
 */
export function getInMatrixComponents(): ComponentCatalogEntry[] {
  return COMPONENT_CATALOG.filter((entry) => entry.inMatrix);
}

/**
 * True when `path` matches a route pattern. Supports an exact match, a nested
 * match (`pattern + '/'`), and a `/**` suffix wildcard — mirroring the
 * semantics used by `roleConfig.canAccessRoute`.
 */
function pathMatchesPattern(path: string, pattern: string): boolean {
  if (pattern.endsWith('/**')) {
    const prefix = pattern.slice(0, -3);
    return path === prefix || path.startsWith(`${prefix}/`);
  }
  return path === pattern || path.startsWith(`${pattern}/`);
}

/**
 * Returns the catalog Component that owns the given route path, or `undefined`
 * if no Component does. A path is owned by a Component when it equals that
 * Component's `entryRoute` or is nested beneath it (`entryRoute + '/'`). When
 * several entries match via nested prefixes, the longest `entryRoute` wins.
 *
 * Used by policy-driven route and navigation gating to map a location to the
 * `componentId` whose Access_Policy admission governs it (Req 10.1, 10.2).
 */
export function getComponentForPath(
  path: string,
): ComponentCatalogEntry | undefined {
  let best: ComponentCatalogEntry | undefined;
  for (const entry of COMPONENT_CATALOG) {
    if (path === entry.entryRoute || path.startsWith(`${entry.entryRoute}/`)) {
      if (best === undefined || entry.entryRoute.length > best.entryRoute.length) {
        best = entry;
      }
    }
  }
  return best;
}

/**
 * True when `path` falls within any Component's owner-only Config_Sub_Route
 * (Req 1.3, 10.4, 15.2). Config_Sub_Routes are never governed by the
 * Access_Matrix grants — they stay restricted to Practice_Owner and
 * Medrender_Owner regardless of a Component's Granted_Principals.
 */
export function isConfigSubRoutePath(path: string): boolean {
  for (const entry of COMPONENT_CATALOG) {
    for (const pattern of entry.configSubRoutes) {
      if (pathMatchesPattern(path, pattern)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Recursively produces a canonical, key-sorted clone of a value so that
 * serialization is independent of object key insertion order. Arrays keep
 * their order (catalog order is semantically meaningful).
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value !== null && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[key] = canonicalize((value as Record<string, unknown>)[key]);
    }
    return sorted;
  }
  return value;
}

/**
 * Deterministic, dependency-free content hash (djb2, 32-bit, hex) over a
 * canonical serialization of the catalog. Identical input bytes yield an
 * identical hash in the frontend (TS) and the vendored backends (JS), so the
 * golden contract test can pin {@link CATALOG_CONTENT_HASH} and fail CI on any
 * cross-branch drift (Decision 3/6).
 */
export function computeCatalogContentHash(
  catalog: readonly ComponentCatalogEntry[],
): string {
  const serialized = JSON.stringify(canonicalize(catalog));
  let hash = 5381;
  for (let i = 0; i < serialized.length; i += 1) {
    // hash * 33 + charCode, kept in 32-bit unsigned range.
    hash = (((hash << 5) + hash) + serialized.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * The pinned content hash of {@link COMPONENT_CATALOG}, asserted by the golden
 * contract test across every branch that vendors the catalog (Req 1.4, 7.3).
 */
export const CATALOG_CONTENT_HASH: string =
  computeCatalogContentHash(COMPONENT_CATALOG);
